import {
    requiredTranslatableZodSchema,
    toTranslatableString,
} from "@@/shared/translations/translation";
import { z } from "zod";
import type { ProductFormFieldId } from "../product-form-field-id";
import {
    productFormFieldIdFactory,
    productFormFieldIdZodSchema,
} from "../product-form-field-id";
import { productFormFieldGroupByZodUnion } from "../product-form-field-quantity";

const _productFormCheckboxAnswerZodObject = z.object({
    _id: productFormFieldIdZodSchema,
    _type: z.literal("PRODUCT_FORM_FIELD_ANSWER"),
    type: z.literal("CHECKBOX"),
    title: requiredTranslatableZodSchema,
    required: z.boolean(),
    quantity: z.number().int().positive().min(1),
    values: z.record(z.coerce.number(), z.boolean()),
    groupedBy: productFormFieldGroupByZodUnion,
});
const productFormCheckboxAnswerZodObject = _productFormCheckboxAnswerZodObject
    .refine(
        data => {
            if (!data.required) return true; // if not required, all doesn't need to be checked
            if (data.quantity !== Object.keys(data.values).length) return false;
            return true;
        },
        data => ({
            message: toTranslatableString({
                sv: "Antalet svar stämmer inte överens med antalet kryssrutor.",
                en: "The number of answers does not match the number of checkboxes.",
            }),
            params: {
                quantity: data.quantity,
                values: data.values,
            },
            path: ["values"],
        }),
    )
    .refine(
        data => {
            if (!data.required) return true;
            if (Object.values(data.values).some(item => !item)) return false;
            return true;
        },
        data => ({
            message: toTranslatableString({
                sv: "En obligatorisk kryssruta måste vara ikryssad.",
                en: "A required checkbox must be checked.",
            }),
            params: {
                required: data.required,
                values: data.values,
            },
            path: ["values"],
        }),
    );

type ProductFormField_CheckboxAnswer = z.infer<
    typeof productFormCheckboxAnswerZodObject
>;

const _factoryParamsZodObject = _productFormCheckboxAnswerZodObject.omit({
    _id: true,
    _type: true,
    type: true,
});

type FactoryParams = z.infer<typeof _factoryParamsZodObject>;
const productFormCheckboxAnswerFactory = (
    input: FactoryParams & {
        _id?: ProductFormFieldId;
    },
): ProductFormField_CheckboxAnswer => {
    return {
        ...input,
        _id: input._id ?? productFormFieldIdFactory(),
        _type: "PRODUCT_FORM_FIELD_ANSWER",
        type: "CHECKBOX",
    };
};

export { productFormCheckboxAnswerFactory, productFormCheckboxAnswerZodObject };
export type {
    ProductFormField_CheckboxAnswer,
    FactoryParams as ProductFormField_CheckboxAnswerFactoryParams,
};
